import { faArrowLeft, faTimes } from '@fortawesome/pro-light-svg-icons';
import { useRouter } from 'next/navigation';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/(pages)/_components/search/search.component';
import { appendRecentSearches, useSearchState } from '@/store/search/search.store';
import { useRecentSearchesStorage } from '@/storage/recents-searches.storage';
import { isVaccineSearchMode } from '@/utils/search/search-vaccine.util';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useLoading } from '@/hooks/use-loading.hook';
import { buildSearchResult } from '@/utils/search/search-result.util';
import { useSearch } from '@/app/_hooks/search/use-search.hook';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { getLogger } from '@/logger/get-logger';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface AskAddMoreExamsProps {
  selectedProcedures: SelectedProcedures[];
  isVaccineSearch?: boolean;
  locationOverride?: OverridedUserLocation;
}

export function AskAddMoreExams({ selectedProcedures, isVaccineSearch, locationOverride }: AskAddMoreExamsProps) {
  const [, setStoredRecentSearches] = useRecentSearchesStorage();

  const { handleJump } = useProgressiveModal<ProgressiveSearchModalKeys>();
  const router = useRouter();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { handleSearchExamsUrl } = useSearch({ locationOverride });
  const analytics = getAnalyticsPlugin();

  const isVaccine = isVaccineSearchMode(useSearchState.getState().selectedSearch, isVaccineSearch);
  const modalTitle = isVaccine ? 'Você adicionou uma vacina' : 'Você adicionou um exame laboratorial';
  const modalDescription = isVaccine
    ? 'Vacinas são preparações que estimulam seu sistema imunológico e protegem contra diversas doenças. Deseja adicionar outra vacina ou revisar suas escolhas antes de prosseguir?'
    : 'Exames laboratoriais são testes feitos em uma pequena amostra do seu corpo como sangue, urina, saliva ou tecido. Deseja acrescentar mais exames antes de ver os valores?';
  const addMoreProceduresTextButton = isVaccine ? 'Acrescentar mais vacinas' : 'Acrescentar mais exames';
  const keepOnlyProcedureTextButton = isVaccine ? 'Manter apenas esta vacina' : 'Manter apenas este exame';

  const handleNavigateToScheduleResult = async (e: React.MouseEvent): Promise<void> => {
    analytics.track(AnalyticsEnum.VIEW_SEARCH_ADD_MORE_EXAMES_OR_KEEP_EXAMS, {
      decision: 'keep_only_this_exam',
    });
    startLoading();
    e.preventDefault();

    try {
      const searchResult = buildSearchResult(selectedProcedures, isVaccine);

      if (searchResult) {
        appendRecentSearches(searchResult, setStoredRecentSearches);
      }

      router.push(handleSearchExamsUrl(isVaccine, selectedProcedures));
      router.refresh();
    } catch (error) {
      const logger = getLogger('SearchModal - AskAddMoreExams', {});
      logger.error('Error updating latest searches', { selectedProcedures, error });
    } finally {
      stopLoading();
    }
  };

  function jumpSelectMoreExams() {
    analytics.track(AnalyticsEnum.VIEW_SEARCH_ADD_MORE_EXAMES_OR_KEEP_EXAMS, {
      decision: 'add_more_exams',
    });
    handleJump({ key: ProgressiveSearchModalKeys.SELECT_MORE_EXAMS });
  }

  return (
    <div
      className="h-full flex flex-col justify-between flex-1 p-5"
      data-testid={DataTestIdEnum.PROCEDURES_MODAL_ASK_ADD_MORE_EXAMS}
    >
      <SheetHeader>
        <div className="flex justify-between gap-2.5 items-start md:items-center">
          <div className="flex gap-4 items-start md:items-center">
            <button
              data-testid="back--ask-add-more-exams"
              onClick={() => {
                if (selectedProcedures.length === 0) {
                  handleJump({ key: ProgressiveSearchModalKeys.SEARCH, returning: true });
                  return;
                }
                handleJump({ key: ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, returning: true });
              }}
            >
              <Icon icon={faArrowLeft} size="xl" className="text-gray-500 self-center cursor-pointer" />
            </button>
            <SheetTitle>
              <div className="flex gap-3">
                <span className="font-extrabold text-2xl text-gray-900 text-wrap text-left">{modalTitle}</span>
              </div>
            </SheetTitle>
          </div>
          <button
            className="text-gray-500 cursor-pointer self-start md:self-center mt-2 md:mt-0 hover:bg-gray-50"
            data-testid="close--ask-add-more-exams"
            onClick={() => {
              if (selectedProcedures.length === 0) {
                handleJump({ key: ProgressiveSearchModalKeys.SEARCH, returning: true });
                return;
              }
              handleJump({ key: ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, returning: true });
            }}
          >
            <Icon icon={faTimes} size="xl" className="hover:text-gray-900 text-[1.625rem]" />
          </button>
        </div>
      </SheetHeader>
      <div className="flex flex-col justify-between h-full">
        <p className="text-gray-800 my-4">{modalDescription}</p>

        <div className="flex flex-col gap-4">
          <Button
            data-testid="add-more-exams--ask-add-more-exams"
            disabled={isLoading}
            onClick={() => jumpSelectMoreExams()}
          >
            {addMoreProceduresTextButton}
          </Button>
          <Button
            variant="secondary"
            data-testid="keep-only-this-exam--ask-add-more-exams"
            isLoading={isLoading}
            onClick={handleNavigateToScheduleResult}
          >
            {keepOnlyProcedureTextButton}
          </Button>
        </div>
      </div>
    </div>
  );
}
